import { getEmailFromSearchParams } from '@/features/auth/emailFromUrl'
import { clearSignerAccountSwitch, readSignerAccountSwitch } from '@/features/docusign/signerAccountSwitch'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { useLocation, useSearchParams, type Location } from 'react-router-dom'

/**
 * Standalone login page for Auth0 passwordless email authentication
 * Implements design from Figma (node-id=19-1920)
 * Checks if user is registered and shows create account page if not
 * @returns {JSX.Element} The standalone login component
 */
const StandaloneLogin: React.FC = () => {
  const { loginWithRedirect, isLoading } = useAuth0()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // Set by the signing page when it signed out the wrong account; names the
  // account the order is assigned to and the order to return to.
  const [signerSwitch] = useState(readSignerAccountSwitch)

  // Preserve the originally requested deep link (set by SecureEHRRoute /
  // StandaloneAuthGuard via ``state={{ from: location }}``) so that after the
  // magic-link round-trip the user lands back where they started (e.g. an
  // /order/results/:id link from a results email) instead of the default page.
  const fromLocation = (location.state as { from?: Location } | null)?.from

  // The signing switch names the clinician's login, which beats the address an
  // emailed deep link was mailed to when the two differ.
  const [email, setEmail] = useState(
    () =>
      getEmailFromSearchParams(searchParams) ||
      signerSwitch?.email ||
      getEmailFromSearchParams(fromLocation?.search) ||
      ''
  )
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const returnTo = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
    : signerSwitch?.returnTo

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailValue)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    // SKIP REGISTRATION CHECK - Go straight to login
    await handleProceedWithLogin()

    // setIsSubmitting(true)

    // try {
    //   // First, check if user is registered
    //   await checkUserRegistration(email)

    //   // The hook will update isRegistered state
    //   // If user is registered, we'll proceed with login in the next render
    //   // If not registered, we'll show the create account page

    //   setIsSubmitting(false)
    // } catch (err) {
    //   Sentry.captureException(err, {
    //     tags: {
    //       operation: 'auth.check-and-login',
    //       flow: 'standalone',
    //     },
    //   })
    //   setError(err instanceof Error ? err.message : 'An error occurred. Please try again.')
    //   setIsSubmitting(false)
    // }
  }

  const handleProceedWithLogin = async () => {
    setIsSubmitting(true)
    setError(null)
    clearSignerAccountSwitch()

    try {
      await Sentry.startSpan(
        {
          op: 'ui.click',
          name: 'Standalone Login - Send Magic Link',
        },
        async (span) => {
          span.setAttribute('auth.flow', 'standalone')
          span.setAttribute('email.domain', email.split('@')[1] || 'unknown')

          await loginWithRedirect({
            ...(returnTo ? { appState: { returnTo } } : {}),
            authorizationParams: {
              connection: 'email',
              login_hint: email,
            },
          })
        }
      )
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          operation: 'auth.login',
          flow: 'standalone',
        },
      })
      setError(err instanceof Error ? err.message : 'Failed to send magic link. Please try again.')
      setIsSubmitting(false)
    }
  }

  // REGISTRATION AND NPI QUIZ DISABLED - Always show login form
  // // Show claim profile form if user clicked "Create Account"
  // if (isRegistered === false && showClaimProfile) {
  //   return <StandaloneClaimProfile email={email} onBack={handleBackToLogin} />
  // }

  // // Show create account welcome page if user is not registered
  // if (isRegistered === false && !showClaimProfile) {
  //   return (
  //     <StandaloneCreateAccount email={email} onBack={handleBackToLogin} onContinue={handleContinueToClaimProfile} />
  //   )
  // }

  // // Show login page if checking or user is registered (or not checked yet)
  // // If user is registered (isRegistered === true), automatically proceed with login
  // if (isRegistered === true && !isSubmitting) {
  //   handleProceedWithLogin()
  // }

  return (
    <div className="relative flex h-screen flex-col items-center justify-center overflow-y-auto bg-[#fafafa] py-8">
      {/* Logo */}
      <div className="mb-12 flex w-full max-w-[610px] justify-center px-8">
        <img
          src="/icons/Inflection_Logo_Dark_LG.png"
          alt="Inflection Medicine"
          className="h-auto max-h-14 w-auto max-w-full object-contain"
        />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[384px] px-8">
        <div className="rounded-base border border-border-base bg-white p-6 shadow-[0px_25px_50px_-12px_rgba(29,41,61,0.25)]">
          <h1 className="mb-6 text-xl font-semibold leading-6 tracking-tight text-text-heading">
            Sign In or Create an Account
          </h1>
          <form onSubmit={handleSubmit} className="space-y-2.5">
            {/* Email Input */}
            <div className="relative flex items-center gap-2 rounded-base border border-border-base-medium bg-[#fafafa] px-3 py-2.5 shadow-xs">
              {/* User Icon */}
              <svg
                className="h-6 w-6 shrink-0 text-text-body-subtle"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>

              {/* Input */}
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting || isLoading}
                className="flex-1 border-0 bg-transparent p-0 text-sm leading-5 text-text-heading placeholder:text-text-body-subtle focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
                placeholder="Enter your email"
                autoComplete="email"
                required
              />

              {/* Close Icon (shown when there's text) */}
              {email && (
                <button
                  type="button"
                  onClick={() => setEmail('')}
                  className="shrink-0 text-text-body-subtle hover:text-text-heading"
                  aria-label="Clear email"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-base bg-error-50 p-3">
                <p className="text-sm text-error-700">{error}</p>
              </div>
            )}

            {/* Continue Button */}
            <button
              type="submit"
              disabled={isSubmitting || isLoading || !email.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-base bg-[#1f7a4d] px-4 py-2.5 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-[#186239] focus:outline-none focus:ring-2 focus:ring-[#1f7a4d] focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              {isSubmitting || isLoading ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Sending magic link...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* DNA Wave Pattern at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[503px] overflow-hidden">
        <img
          src="/icons/INFL Wave B 2026 - on dark.svg"
          alt=""
          className="h-full w-full object-cover"
          aria-hidden="true"
        />
      </div>
    </div>
  )
}

export default StandaloneLogin
